/* eslint-disable no-empty */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DeleteIcon from "src/assets/all-icons/delete-icon.jsx";

import DropdownArrowIcon from "@/assets/all-icons/dropdown-arrow-icon";
import config from "@/config";
import useStateHandler from "@/hooks/useStateHandler";
import fetchWrapper from "@/services/fetchWrapper.service";
import Loader from "@/shared-components/loader/Loader";
import { getStickerImageId } from "@/shared-components/stickers/utils";
import Utils from "@/utils";
import { resolveUserProfileImage, resolveUserProfileTypeWithFallback } from "@/utils/userProfileImage";

import UserProfileImage from "./UserProfileImage";

import "./styles/ChatMessage.css";

const usernameImageCache = new Map();

function normalizeVideoUrl(url) {
  if (!url || typeof url !== "string") return url;
  try {
    const u = new URL(url, window.location.origin);
    const host = u.hostname;
    const isCFStream =
      /cloudflarestream\.com$/i.test(host) || /videodelivery\.net$/i.test(host);

    if (isCFStream && /\/manifest\/.+video\.m3u8$/i.test(u.pathname)) {
      // HLS → MP4 directo (si está disponible en CF)
      return url.replace(/\/manifest\/.+video\.m3u8$/i, "/downloads/default.mp4");
    }
  } catch (_) {
    /* ignore */
  }
  return url;
}

const resolveId = (value) => {
  if (!value) return null;
  if (typeof value === "object") {
    return value._id ?? value.id ?? value.user_id ?? value.userId ?? null;
  }
  return value;
};

const pickFirst = (...vals) => {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
};

function getReplyContext(message, currentUser = null) {
  if (!message || typeof message !== "object") return null;
  const reply =
    message?.metadata?.reply ||
    message?.message_metadata?.reply ||
    message?.reply ||
    message?.message_reply ||
    null;

  if (!reply || typeof reply !== "object") return null;

  const messageId =
    reply?.messageId ||
    reply?.message_id ||
    reply?.parentMessageId ||
    reply?.parent_message_id ||
    reply?.replyToMessageId ||
    reply?.reply_to_message_id ||
    reply?.message?._id ||
    reply?.message?.id ||
    null;

  const senderId = reply?.senderId || reply?.sender_id || reply?.message_sender_id || null;
  const senderUsername =
    reply?.senderUsername ||
    reply?.sender_username ||
    reply?.message_sender_username ||
    reply?.user_username ||
    reply?.sender?.user_username ||
    null;

  const messageText =
    pickFirst(
      reply?.previewText,
      reply?.preview_text,
      reply?.message_text,
      reply?.text,
      reply?.message?.message_text,
      reply?.message?.text
    ) || "";

  const mediaType = String(
    reply?.mediaType ||
    reply?.media_type ||
    reply?.message?.message_media?.type ||
    reply?.message?.media?.type ||
    ""
  ).toLowerCase();
  const hasSticker = Boolean(reply?.hasSticker || reply?.message_sticker || reply?.sticker);
  const hasMedia = Boolean(
    reply?.hasMedia ||
    reply?.message_media ||
    reply?.media ||
    reply?.message?.message_media ||
    reply?.message?.media
  );

  let previewText = messageText.trim();
  if (!previewText) {
    if (hasSticker) previewText = "Sticker";
    else if (hasMedia) previewText = mediaType === "video" ? "Video" : mediaType === "audio" ? "Audio" : "Image";
    else previewText = "Message";
  }
  if (previewText.length > 90) {
    previewText = `${previewText.slice(0, 87)}...`;
  }

  let displayName = senderUsername || "Unknown user";
  const currentUserId = resolveId(currentUser);
  const currentUsername = currentUser?.user_username || null;
  if (
    (currentUserId && senderId && String(currentUserId) === String(senderId))
    || (currentUsername && senderUsername && currentUsername === senderUsername)
  ) {
    displayName = "You";
  }

  return {
    messageId: messageId ? String(messageId) : null,
    senderUsername: displayName,
    previewText,
  };
}

// /w=### para imágenes en nuestros hosts
const withWidth = (rawUrl, w = 256) => {
  if (!rawUrl) return null;
  if (/^(data:|blob:)/i.test(rawUrl)) return rawUrl;
  if (/\/w=\d+$/i.test(rawUrl)) return rawUrl.replace(/\/w=\d+$/i, `/w=${w}`);

  let url;
  try {
    url = new URL(rawUrl, window.location.origin);
  } catch {
    return rawUrl;
  }
  const host = url.hostname.toLowerCase();

  let imageHost = null;
  try {
    if (config?.app?.imageServiceDomain) {
      imageHost = new URL(config.app.imageServiceDomain).hostname.toLowerCase();
    }
  } catch {
    /* ignore */
  }

  const isImageDelivery = host.endsWith("imagedelivery.net");
  const isOurImageHost = !!imageHost && host === imageHost;

  const path = url.pathname;
  const parts = path.split("/").filter(Boolean);

  if (isImageDelivery) {
    if (parts.length >= 3) {
      parts[parts.length - 1] = `w=${w}`;
      url.pathname = "/" + parts.join("/");
      url.search = "";
      return url.toString();
    }
    url.pathname = path.replace(/\/$/, "") + `/w=${w}`;
    url.search = "";
    return url.toString();
  }

  if (isOurImageHost) {
    url.pathname = path.replace(/\/$/, "") + `/w=${w}`;
    url.search = "";
    return url.toString();
  }

  const sep = url.search ? "&" : "?";
  url.search = (url.search || "") + `${sep}w=${w}`;
  return url.toString();
};

// ---- util: extraer dimensiones del media para calcular aspect ratio ----
function getMediaDimensions(rawMedia) {
  if (!rawMedia || typeof rawMedia !== "object") return null;

  const w =
    rawMedia.width ??
    rawMedia.w ??
    rawMedia.videoWidth ??
    rawMedia.imageWidth ??
    rawMedia.dimensions?.width ??
    rawMedia.meta?.width ??
    null;

  const h =
    rawMedia.height ??
    rawMedia.h ??
    rawMedia.videoHeight ??
    rawMedia.imageHeight ??
    rawMedia.dimensions?.height ??
    rawMedia.meta?.height ??
    null;

  if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
    return { width: Number(w), height: Number(h) };
  }
  return null;
}

function ChatMessage({
  messageData,
  showAvatar = true,
  isMineRight = true,
  isTargeted = false,
  onNavigateToMessage = null,
  canReply = false,
  onReply = null,
  canDelete = false,
  onDelete = null,
  deleting = false,
  sending = false,
  /** opcional: forzar tipo de perfil (p.ej. "gallery") */
  profileTypeOverride = null,
}) {
  const { state } = useStateHandler();
  const messageRef = useRef(null);
  const messageContainerRef = useRef(null);
  const [isSingleLine, setIsSingleLine] = useState(false);
  const [resolvedImageFromUsername, setResolvedImageFromUsername] = useState(null);
  const [isActionSelected, setIsActionSelected] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);

  const rawSender =
    (messageData?.message_sender && typeof messageData.message_sender === "object" && messageData.message_sender) ||
    (messageData?.user && typeof messageData.user === "object" && messageData.user) ||
    (messageData?.sender && typeof messageData.sender === "object" && messageData.sender) ||
    null;
  const sender = rawSender ? { ...rawSender } : {};

  const messageText = (messageData?.message_text ?? messageData?.text ?? "") || "";
  const isStickerMessage = Boolean(messageData?.message_sticker);
  const stickerImageId = isStickerMessage ? getStickerImageId(messageData.message_sticker) : null;
  const stickerUrl = stickerImageId ? `${config.app.imageServiceDomain}/${stickerImageId}/w=256` : null;

  // ---- MEDIA NORMALIZATION ----
  const rawMedia =
    (messageData?.media && typeof messageData.media === "object" && messageData.media) ||
    (messageData?.message_media && typeof messageData.message_media === "object" && messageData.message_media) ||
    null;

  const mediaType = rawMedia?.type ? String(rawMedia.type).toLowerCase() : "";

  const basePreview = pickFirst(
    rawMedia?.previewUrl,
    rawMedia?.preview_url,
    rawMedia?.thumbnailUrl,
    rawMedia?.thumbnail_url,
    rawMedia?.image
  );
  const basePlayback = pickFirst(
    rawMedia?.playbackUrl,
    rawMedia?.playback_url,
    rawMedia?.deliveryUrl,
    rawMedia?.delivery_url
  );
  const baseOriginal = pickFirst(rawMedia?.originalUrl, rawMedia?.original_url, rawMedia?.url);

  const mediaPreviewUrl = mediaType === "video" ? basePreview || baseOriginal : withWidth(basePreview || baseOriginal, 512);
  const mediaFullImageUrl = mediaType === "video" ? null : withWidth(baseOriginal || basePreview || basePlayback, 1200);

  const rawPlaybackUrl = basePlayback || baseOriginal || null;
  const mediaPlaybackUrl = normalizeVideoUrl(rawPlaybackUrl);

  const hasMediaAttachment = Boolean(rawMedia && (mediaPreviewUrl || mediaPlaybackUrl));

  // ---- dimensiones del media para ajustar la burbuja (vertical/horizontal) ----
  const dims = useMemo(() => getMediaDimensions(rawMedia), [rawMedia]);
  const aspectRatio = useMemo(() => {
    if (!dims) return null;
    return dims.width / dims.height;
  }, [dims]);

  // límites de tamaño de burbuja
  const MAX_W = 320;
  const MAX_H = 320;
  const MIN_W = 160; // evita burbujas demasiado estrechas en vertical extremo

  // calculamos tamaño final de la "caja" del thumbnail, manteniendo aspect
  const mediaBoxSize = useMemo(() => {
    if (!aspectRatio || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
      // fallback cuando no sabemos el aspect ratio
      return { width: MAX_W, height: Math.round(MAX_W * 9 / 16) };
    }

    // Si es landscape (>=1), priorizamos ancho máximo
    if (aspectRatio >= 1) {
      const width = MAX_W;
      const height = Math.min(MAX_H, Math.round(width / aspectRatio));
      return { width, height };
    }

    // Vertical: priorizamos altura máxima y calculamos ancho
    const height = MAX_H;
    let width = Math.round(height * aspectRatio);
    if (width < MIN_W) width = MIN_W; // quepa el play entero
    // nunca pasar ancho máximo
    width = Math.min(width, MAX_W);
    return { width, height };
  }, [aspectRatio]);

  // ---- LIGHTBOX (solo carga video al abrir) ----
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxType, setLightboxType] = useState(null); // 'image' | 'video'
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxPoster, setLightboxPoster] = useState(null);
  const lightboxVideoRef = useRef(null);
  const [hlsInstance, setHlsInstance] = useState(null);

  const openLightbox = useCallback(() => {
    if (!hasMediaAttachment) return;
    const type = mediaType === "video" ? "video" : "image";
    const src = type === "video" ? mediaPlaybackUrl || mediaPreviewUrl : mediaFullImageUrl || mediaPreviewUrl;
    if (!src) return;
    setLightboxType(type);
    setLightboxSrc(src);
    setLightboxPoster(mediaPreviewUrl || null);
    setLightboxOpen(true);
  }, [hasMediaAttachment, mediaType, mediaPlaybackUrl, mediaPreviewUrl, mediaFullImageUrl]);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setLightboxType(null);
    setLightboxSrc(null);
    setLightboxPoster(null);
  }, []);

  // Montaje de HLS sólo si hace falta en el lightbox
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!lightboxOpen || lightboxType !== "video" || !lightboxSrc) return;

      // si es m3u8 y no hay soporte nativo -> usar hls.js on-demand
      const isM3U8 = /\.m3u8(\?|$)/i.test(lightboxSrc);
      const videoEl = lightboxVideoRef.current;
      if (!videoEl) return;

      const canPlayHlsNatively = !!videoEl.canPlayType && videoEl.canPlayType("application/vnd.apple.mpegurl");

      if (isM3U8 && !canPlayHlsNatively) {
        try {
          const { default: Hls } = await import("hls.js/dist/hls.min.js");
          if (!cancelled && Hls && Hls.isSupported()) {
            const h = new Hls();
            h.loadSource(lightboxSrc);
            h.attachMedia(videoEl);
            setHlsInstance(h);
          }
        } catch (e) {
          // si falla, el usuario verá que no carga ese formato en su navegador
        }
      }
    })();

    return () => {
      // cleanup hls instance al cerrar
      if (hlsInstance) {
        try {
          hlsInstance.destroy();
        } catch {}
        setHlsInstance(null);
      }
      cancelled = true;
    };
  }, [lightboxOpen, lightboxType, lightboxSrc]); // no dependas de hlsInstance aquí

  // ---- DELETE ----
  const showReplyAction = Boolean(canReply && typeof onReply === "function");
  const showDeleteAction = Boolean(canDelete && typeof onDelete === "function");
  const showMessageActions = showReplyAction || showDeleteAction;
  const isTouchNavigation = useCallback(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(hover: none)").matches || window.matchMedia("(pointer: coarse)").matches;
  }, []);

  const closeActionMenu = useCallback(() => {
    setIsActionMenuOpen(false);
    setIsActionSelected(false);
  }, []);

  const handleDelete = () => {
    if (!showDeleteAction || deleting) return;
    closeActionMenu();
    onDelete(messageData);
  };

  const handleReply = useCallback(() => {
    if (!showReplyAction) return;
    closeActionMenu();
    onReply(messageData);
  }, [closeActionMenu, messageData, onReply, showReplyAction]);

  const handleToggleActionMenu = useCallback((event) => {
    event.stopPropagation();
    if (!showMessageActions || deleting) return;
    setIsActionSelected(true);
    setIsActionMenuOpen((prev) => !prev);
  }, [deleting, showMessageActions]);

  const handleMessageContainerClick = useCallback((event) => {
    if (!showMessageActions || !isTouchNavigation()) return;
    if (event.target.closest(".chat-message__action-area")) return;
    if (isActionMenuOpen) return;
    setIsActionSelected((prev) => !prev);
  }, [isActionMenuOpen, isTouchNavigation, showMessageActions]);

  useEffect(() => {
    if (!showMessageActions || (!isActionSelected && !isActionMenuOpen)) return;

    const handleOutside = (event) => {
      if (messageContainerRef.current?.contains(event.target)) return;
      closeActionMenu();
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [closeActionMenu, isActionMenuOpen, isActionSelected, showMessageActions]);

  // ---- CURRENT USER VS SENDER ----
  const senderId =
    resolveId(sender) ??
    resolveId(messageData?.message_sender) ??
    resolveId(messageData?.user) ??
    resolveId(messageData?.sender) ??
    messageData?.message_sender_id ??
    messageData?.sender_id ??
    messageData?.user_id ??
    messageData?.userId ??
    (typeof messageData?.message_sender === "string" ? messageData.message_sender : null);

  const currentUser = state?.user_session || null;
  const currentUserId = resolveId(currentUser);
  const currentUsername = currentUser?.user_username || null;
  const replyContext = useMemo(() => getReplyContext(messageData, currentUser), [messageData, currentUser]);
  const canNavigateToReplyMessage = Boolean(
    replyContext?.messageId && typeof onNavigateToMessage === "function"
  );
  const handleReplyPreviewClick = useCallback((event) => {
    if (!canNavigateToReplyMessage) return;
    event.stopPropagation();
    onNavigateToMessage(replyContext.messageId);
  }, [canNavigateToReplyMessage, onNavigateToMessage, replyContext?.messageId]);

  const senderUsername =
    sender?.user_username || messageData?.user_username || messageData?.message_sender_username || null;

  const explicitMine =
    typeof messageData?.isMine === "boolean"
      ? messageData.isMine
      : typeof messageData?.is_owner === "boolean"
        ? messageData.is_owner
        : typeof messageData?.isOwner === "boolean"
          ? messageData.isOwner
          : null;

  const isMine =
    explicitMine !== null
      ? explicitMine
      : currentUserId && senderId
        ? String(currentUserId) === String(senderId)
        : currentUsername && senderUsername
          ? currentUsername === senderUsername
          : false;

  const senderImageRaw = sender?.user_image ?? messageData?.user_image ?? null;
  const senderImage = resolveUserProfileImage(senderImageRaw);

  useEffect(() => {
    let cancelled = false;
    async function resolveByUsername(username) {
      if (!username || usernameImageCache.has(username)) {
        setResolvedImageFromUsername(username ? usernameImageCache.get(username) || null : null);
        return;
      }
      try {
        const { response, data } = await fetchWrapper.get(
          config.apis.api.url + "/user/public/" + encodeURIComponent(username)
        );
        if (!cancelled && response?.ok) {
          const img = resolveUserProfileImage(
            data?.user_image,
            data?.profile_image,
            data?.image,
            data?.userProfileImage,
          );
          if (img) {
            usernameImageCache.set(username, img);
            setResolvedImageFromUsername(img);
          }
        }
      } catch {
        // ignore
      }
    }

    if (!senderImage && senderUsername) {
      resolveByUsername(senderUsername);
    } else {
      setResolvedImageFromUsername(null);
    }

    return () => {
      cancelled = true;
    };
  }, [senderImage, senderUsername]);

  const effectiveImage = senderImage || resolvedImageFromUsername;

  const senderTypeCandidates = [
    profileTypeOverride,
    sender?.user_type,
    sender?.typeOfAccount,
    sender?.type_of_account,
    messageData?.user?.user_type,
    messageData?.user?.typeOfAccount,
    messageData?.user?.type_of_account,
    messageData?.message_sender?.user_type,
    messageData?.message_sender?.typeOfAccount,
    messageData?.message_sender?.type_of_account,
    messageData?.user_type,
    messageData?.message_sender_user_type,
    messageData?.sender?.user_type,
    messageData?.sender_user_type,
  ];
  if (isMine && currentUser?.user_type) {
    senderTypeCandidates.push(currentUser.user_type);
  }
  const senderType = resolveUserProfileTypeWithFallback("collector", ...senderTypeCandidates);

  const profileLink = senderUsername ? "/user/" + senderUsername : null;
  const messageTimestamp = messageData?.createdAt ?? messageData?.created_at ?? Date.now();
  const formattedTime = Utils.getTimeOfMessageFromTimestamp(new Date(messageTimestamp).getTime());
  const isSendingMessage = Boolean(sending || messageData?.isSending);

  // ---- single-line calc para texto puro ----
  useEffect(() => {
    if (isStickerMessage || hasMediaAttachment) {
      setIsSingleLine(false);
      return;
    }
    if (messageRef.current) {
      const computedStyle = window.getComputedStyle(messageRef.current);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const messageHeight = messageRef.current.offsetHeight;
      setIsSingleLine(messageHeight <= lineHeight * 1.5);
    } else {
      setIsSingleLine(false);
    }
  }, [messageText, isStickerMessage, hasMediaAttachment]);

  const containerClasses = ["chat-message__container"];
  if (isMine) containerClasses.push("is-mine");
  if (isSingleLine) containerClasses.push("single-line");

  const messageContainerClasses = ["chat-message__message-container"];
  if (isStickerMessage) messageContainerClasses.push("sticker");
  if (hasMediaAttachment) messageContainerClasses.push("has-media");
  if (isTargeted) messageContainerClasses.push("is-targeted");
  if (showMessageActions && (isActionSelected || isActionMenuOpen)) {
    messageContainerClasses.push("show-action-control");
  }

  // ---- thumbnail en burbuja (centrado + respeta vertical) ----
  const mediaBoxStyle = hasMediaAttachment
    ? {
      width: `${mediaBoxSize.width}px`,
      height: `${mediaBoxSize.height}px`,
    }
    : undefined;

  const mediaElement =
    mediaType === "video" ? (
      <button
        type="button"
        className="chat-message__media-thumb"
        onClick={openLightbox}
        aria-label="Reproducir video"
        style={mediaBoxStyle}
      >
        <img
          className="chat-message__media-image"
          src={mediaPreviewUrl || ""}
          alt="Miniatura de video"
          loading="lazy"
          decoding="async"
        />
        <span className="chat-message__media-play" />
      </button>
    ) : (
      <div
        className="chat-message__media-thumb"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openLightbox()}
        onClick={openLightbox}
        style={mediaBoxStyle}
      >
        <img
          className="chat-message__media-image"
          src={mediaPreviewUrl || ""}
          alt="Media attachment"
          loading="lazy"
          decoding="async"
        />
      </div>
    );

  const renderAvatar = () => (
    <UserProfileImage image={effectiveImage} typeOfProfile={senderType} className="chat-message__user-image" />
  );

  const avatarNode = profileLink ? (
    <Link className="no-underline" to={profileLink} state={{ from: true }}>
      {renderAvatar()}
    </Link>
  ) : (
    renderAvatar()
  );

  const replyPreviewNode = replyContext ? (
    <button
      type="button"
      className={`chat-message__reply-preview ${canNavigateToReplyMessage ? "is-clickable" : ""}`}
      onClick={handleReplyPreviewClick}
      disabled={!canNavigateToReplyMessage}
      aria-label={canNavigateToReplyMessage ? "Jump to replied message" : undefined}
      title={canNavigateToReplyMessage ? "Go to replied message" : undefined}
    >
      <span className="chat-message__reply-preview-user">{replyContext.senderUsername}</span>
      <span className="chat-message__reply-preview-text">{replyContext.previewText}</span>
    </button>
  ) : null;

  return (
    <div className={containerClasses.join(" ")} data-channel-type={messageData?.channelType || ""} data-channel-id={messageData?.channelId || ""}>
      {showAvatar && !isMine && avatarNode}
      <div className={messageContainerClasses.join(" ")} ref={messageContainerRef} onClick={handleMessageContainerClick}>
        {showMessageActions && (
          <div className={`chat-message__action-area ${isActionMenuOpen ? "active" : ""}`} onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="chat-message__action-trigger"
              onClick={handleToggleActionMenu}
              disabled={deleting}
              aria-label="Message actions"
              title="Message actions"
              aria-expanded={isActionMenuOpen}
              aria-haspopup="menu"
            >
              <DropdownArrowIcon className="chat-message__action-trigger-icon" />
            </button>
            <div className={`chat-message__action-menu ${isActionMenuOpen ? "active" : ""}`} role="menu">
              {showReplyAction ? (
                <button
                  type="button"
                  className="chat-message__action-menu-item chat-message__action-menu-item--reply"
                  onClick={handleReply}
                  role="menuitem"
                  aria-label="Reply to message"
                >
                  <span>Reply</span>
                </button>
              ) : null}
              {showDeleteAction ? (
                <button
                  type="button"
                  className="chat-message__action-menu-item chat-message__action-menu-item--delete"
                  onClick={handleDelete}
                  disabled={deleting}
                  role="menuitem"
                  aria-label="Delete message"
                >
                  {deleting ? <span className="chat-message__action-spinner" /> : <DeleteIcon className="chat-message__action-icon" />}
                  <span>Delete</span>
                </button>
              ) : null}
            </div>
          </div>
        )}

        {isStickerMessage ? (
          <>
            {replyPreviewNode}
            {stickerUrl ? (
              <img src={stickerUrl} alt={messageData?.message_sticker?.sticker_name || "Sticker"} className="chat-message__sticker" loading="lazy" />
            ) : (
              <div className="chat-message__sticker chat-message__sticker--fallback font-size-medium">Sticker</div>
            )}
            <div className="chat-message__meta">
              {isSendingMessage ? <Loader inline thin className="chat-message__send-loader" /> : null}
              <span className="chat-message__time st-l">{formattedTime}</span>
            </div>
          </>
        ) : (
          <>
            {replyPreviewNode}
            <div className="chat-message__content">
              {hasMediaAttachment ? (
                <div className={"chat-message__media " + (mediaType || "image")}>{mediaElement}</div>
              ) : null}
              {messageText ? (
                <p ref={messageRef} className="chat-message__message font-size-medium">
                  {messageText}
                </p>
              ) : null}
            </div>
            <div className="chat-message__meta">
              {isSendingMessage ? <Loader inline thin className="chat-message__send-loader" /> : null}
              <span className="chat-message__time st-l">{formattedTime}</span>
            </div>
          </>
        )}
      </div>
      {showAvatar && isMine && isMineRight && avatarNode}

      {/* --- LIGHTBOX --- */}
      {lightboxOpen && (
        <div className="chat-message__lightbox" onClick={closeLightbox} role="dialog" aria-modal="true" aria-label="Media viewer">
          <div className="chat-message__lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="chat-message__lightbox-close" onClick={closeLightbox} aria-label="Close" title="Close">
              ×
            </button>

            {lightboxType === "video" ? (
              <video
                key={lightboxSrc} // fuerza remonte
                ref={lightboxVideoRef}
                className="chat-message__lightbox-video"
                // Si es m3u8, dejamos que HLS lo cargue. Si no, ponemos src directo.
                src={/\.m3u8(\?|$)/i.test(lightboxSrc) ? undefined : lightboxSrc || undefined}
                poster={lightboxPoster || undefined}
                controls
                autoPlay
                playsInline
                preload="auto"
              />
            ) : (
              <img className="chat-message__lightbox-image" src={lightboxSrc || ""} alt="Media" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(ChatMessage);
